// @ts-strict-ignore
import { v4 as uuid } from 'uuid'
import { startTransition, useCallback, useMemo, useState } from 'react'
import constate from 'constate'

interface IMutationInProgress {
  mutationId: string
  nodeId?: string
}

interface IMutationInProgressInput {
  mutationId?: string
  nodeId?: string
}

function useMutationTracker() {
  const [mutationsInProgress, setMutationsInProgress] = useState<IMutationInProgress[]>([])

  const startMutation = useCallback((newMutation?: IMutationInProgressInput) => {
    const newMutationWithId = {
      ...(newMutation || {}),
      mutationId: newMutation.mutationId || uuid(),
    }
    startTransition(() => {
      setMutationsInProgress([...mutationsInProgress, newMutationWithId])
    })
    return newMutationWithId
  }, [mutationsInProgress])

  const endMutation = useCallback((mutationId: string) => {
    const endedMutation = mutationsInProgress.find(m => m?.mutationId === mutationId)
    startTransition(() => {
      setMutationsInProgress(mutationsInProgress.filter(m => m?.mutationId !== mutationId))
    })
    return endedMutation
  }, [mutationsInProgress])

  return {
    mutationsInProgress,
    startMutation,
    endMutation,
  }
}

// NODE: This unfortunately is not (and cannot be) a static export
export const [
  MutationTrackerProvider,
  useMutationsInProgress,
  useStartMutation,
  useEndMutation,
] = constate(
  useMutationTracker,
  value => value.mutationsInProgress, // becomes useMutationsInProgress
  value => value.startMutation, // becomes useStartMutation
  value => value.endMutation, // becomes useEndMutation
)

export function useIsMutating(nodeId: string) {
  const mutationsInProgress = useMutationsInProgress()
  return useMemo(() => {
    if (!nodeId) return null

    return Boolean(mutationsInProgress.find(m => m?.nodeId === nodeId))
  }, [mutationsInProgress, nodeId])
}
